/**
 * FAQ Page - Dashboard Version
 *
 * Displays FAQ content with accordion UI, search functionality,
 * and markdown rendering with code highlighting.
 *
 * Integrated into user dashboard with DashboardLayout.
 */

import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import faqService, { getTranslatedText } from '../../services/faq';
import type { FaqCategoryWithQuestions, FaqQuestion } from '../../types/faq';

export default function FaqPage() {
  const { t, i18n } = useTranslation('faq');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [expandedQuestions, setExpandedQuestions] = useState<Set<number>>(new Set());
  const [filteredCategories, setFilteredCategories] = useState<FaqCategoryWithQuestions[]>([]);

  // Fetch all FAQ categories with published questions
  const { data, isLoading, error } = useQuery({
    queryKey: ['faq', i18n.language],
    queryFn: () => faqService.getPublicFaqs(),
  });

  // Debounce search input (300ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Filter categories based on search query
  useEffect(() => {
    if (!data?.categories) {
      return;
    }

    if (!debouncedQuery.trim()) {
      setFilteredCategories(data.categories);
      return;
    }

    const query = debouncedQuery.toLowerCase();
    const filtered = data.categories
      .map((category) => ({
        ...category,
        questions: category.questions.filter(
          (q) =>
            q.question.toLowerCase().includes(query) ||
            q.answer.toLowerCase().includes(query)
        ),
      }))
      .filter((category) => category.questions.length > 0);

    setFilteredCategories(filtered);

    // Auto-expand first 3 matching questions
    const matchingIds = filtered
      .flatMap((cat) => cat.questions)
      .slice(0, 3)
      .map((q) => q.id);
    setExpandedQuestions(new Set(matchingIds));
  }, [debouncedQuery, data]);

  // Toggle question expansion
  const toggleQuestion = (questionId: number) => {
    setExpandedQuestions((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(questionId)) {
        newSet.delete(questionId);
      } else {
        newSet.add(questionId);
      }
      return newSet;
    });
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">{t('loading')}</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border-2 border-red-200 rounded-lg p-6">
          <div className="flex items-start gap-3">
            <svg
              className="w-6 h-6 text-red-500 flex-shrink-0"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <div>
              <h2 className="text-lg font-semibold text-red-900">{t('error.title')}</h2>
              <p className="mt-1 text-red-800">{t('error.message')}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">{t('title')}</h1>
        <p className="mt-2 text-gray-600">{t('subtitle')}</p>
      </div>

      {/* Search Bar */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="relative max-w-xl">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg
              className="h-5 w-5 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('search_placeholder')}
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          />
        </div>
      </div>

      {/* FAQ Content */}
      <div>
        {filteredCategories.length === 0 ? (
          // No results
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <h3 className="mt-4 text-lg font-medium text-gray-900">{t('no_results.title')}</h3>
            <p className="mt-2 text-sm text-gray-500">
              {t('no_results.message')}
            </p>
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-indigo-700 bg-indigo-100 hover:bg-indigo-200"
              >
                {t('no_results.clear_search')}
              </button>
            )}
          </div>
        ) : (
          // FAQ Categories
          <div className="space-y-6">
            {filteredCategories.map((category) => (
              <div key={category.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                {/* Category Header */}
                <div className="bg-gradient-to-r from-indigo-50 to-blue-50 px-6 py-4 border-b border-gray-200">
                  <h2 className="text-xl font-semibold text-gray-900">
                    {getTranslatedText(category.name, category.name_translations, i18n.language)}
                  </h2>
                  {category.description && (
                    <p className="mt-1 text-sm text-gray-600">
                      {getTranslatedText(category.description, category.description_translations, i18n.language)}
                    </p>
                  )}
                </div>

                {/* Questions */}
                <div className="divide-y divide-gray-200">
                  {category.questions.map((question) => (
                    <QuestionAccordion
                      key={question.id}
                      question={question}
                      isExpanded={expandedQuestions.has(question.id)}
                      onToggle={() => toggleQuestion(question.id)}
                      lang={i18n.language}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Help Section */}
        <div className="mt-6 bg-gradient-to-r from-indigo-50 to-blue-50 rounded-lg border-2 border-indigo-100 p-6">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-indigo-600 flex items-center justify-center flex-shrink-0">
              <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900">{t('help.title')}</h3>
              <p className="mt-1 text-gray-700">{t('help.message')}</p>
              <Link
                to="/tickets/new"
                className="mt-3 inline-flex items-center px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors"
              >
                {t('help.contact_support')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Question Accordion Component
interface QuestionAccordionProps {
  question: FaqQuestion;
  isExpanded: boolean;
  onToggle: () => void;
  lang: string;
}

function QuestionAccordion({ question, isExpanded, onToggle, lang }: QuestionAccordionProps) {
  const displayQuestion = getTranslatedText(question.question, question.question_translations, lang);
  const displayAnswer = getTranslatedText(question.answer, question.answer_translations, lang);
  return (
    <div>
      {/* Question Header (clickable) */}
      <button
        onClick={onToggle}
        className="w-full px-6 py-4 text-left hover:bg-gray-50 transition-colors focus:outline-none focus:bg-gray-50"
      >
        <div className="flex items-center justify-between">
          <h3 className="text-base font-medium text-gray-900 pr-8">{displayQuestion}</h3>
          <div className="flex items-center gap-4">
            {/* Chevron Icon */}
            <svg
              className={`w-5 h-5 text-gray-400 transition-transform ${
                isExpanded ? 'transform rotate-180' : ''
              }`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </button>

      {/* Answer (collapsible) */}
      {isExpanded && (
        <div className="px-6 pb-6 pt-2">
          <div className="prose prose-sm max-w-none text-gray-700">
            <ReactMarkdown
              components={{
                code({ node, inline, className, children, ...props }) {
                  const match = /language-(\w+)/.exec(className || '');
                  return !inline && match ? (
                    <SyntaxHighlighter
                      style={vscDarkPlus}
                      language={match[1]}
                      PreTag="div"
                      {...props}
                    >
                      {String(children).replace(/\n$/, '')}
                    </SyntaxHighlighter>
                  ) : (
                    <code className={className} {...props}>
                      {children}
                    </code>
                  );
                },
              }}
            >
              {displayAnswer}
            </ReactMarkdown>
          </div>
        </div>
      )}
    </div>
  );
}
